import {
  Alert,
  Button,
  Col,
  Form,
  OverlayTrigger,
  Row,
  Table,
  Tooltip,
} from 'react-bootstrap';
import { CountrySelect } from '../common/CountrySelect';
import DropdownSelect from '../common/DropdownSelect';
import type {
  EchaCandidateSubstance,
  PackagingItem,
  ProductForm,
  SupplyChainTraceabilityRow,
  UpdateMaterial,
  UpdatePackagingItem,
  UpdateProductForm,
  UpdateSupplyChainSupplier,
  UpdateSvhcDeclaration,
} from './productFormTypes';

type Props = {
  form: ProductForm;
  isTextile: boolean;
  isElectronics: boolean;
  bomTotal: number;
  validationErrors: Partial<Record<string, string>>;
  echaCandidateSubstances: EchaCandidateSubstance[];
  existingRohsSupportingDocumentName: string | null;
  existingRohsSupportingDocumentSignedUrl: string | null;
  removeExistingRohsSupportingDocument: boolean;
  existingPopsPfasDocumentName: string | null;
  existingPopsPfasDocumentSignedUrl: string | null;
  removeExistingPopsPfasDocument: boolean;
  updateForm: UpdateProductForm;
  updateMaterial: UpdateMaterial;
  updatePackagingItem: UpdatePackagingItem;
  updateSupplyChainSupplier: UpdateSupplyChainSupplier;
  updateSvhcDeclaration: UpdateSvhcDeclaration;
  addPackagingItem: () => void;
  addSupplyChainSupplier: () => void;
  addSvhcDeclaration: () => void;
  handleSvhcSubstanceChange: (index: number, substanceName: string) => void;
  onRemoveExistingRohsSupportingDocument: () => void | Promise<void>;
  onRohsSupportingDocumentFileChange: (
    file: File | null,
  ) => void | Promise<void>;
  onRemoveExistingPopsPfasDocument: () => void | Promise<void>;
  onPopsPfasTestReportFileChange: (file: File | null) => void | Promise<void>;
};

export function AddProductStepCompliance({
  form,
  isTextile,
  isElectronics,
  bomTotal,
  validationErrors,
  echaCandidateSubstances,
  existingRohsSupportingDocumentName,
  existingRohsSupportingDocumentSignedUrl,
  removeExistingRohsSupportingDocument,
  existingPopsPfasDocumentName,
  existingPopsPfasDocumentSignedUrl,
  removeExistingPopsPfasDocument,
  updateForm,
  updateMaterial,
  updatePackagingItem,
  updateSupplyChainSupplier,
  updateSvhcDeclaration,
  addPackagingItem,
  addSupplyChainSupplier,
  addSvhcDeclaration,
  handleSvhcSubstanceChange,
  onRemoveExistingRohsSupportingDocument,
  onRohsSupportingDocumentFileChange,
  onRemoveExistingPopsPfasDocument,
  onPopsPfasTestReportFileChange,
}: Props) {
  const validationMessages = Object.values(validationErrors).filter(Boolean);

  const shortenFileName = (value: string, maxLength = 28) => {
    if (value.length <= maxLength) return value;

    const extensionIndex = value.lastIndexOf('.');
    const hasExtension =
      extensionIndex > 0 && extensionIndex >= value.length - 8;
    const extension = hasExtension ? value.slice(extensionIndex) : '';
    const baseName = hasExtension ? value.slice(0, extensionIndex) : value;
    const availableBaseLength = Math.max(8, maxLength - extension.length - 1);

    return `${baseName.slice(0, availableBaseLength)}...${extension}`;
  };

  const rohsExemptionOptions = [
    {
      value: '6(a)',
      label: '6(a) - Lead as an alloying element in steel',
    },
    {
      value: '6(b)',
      label: '6(b) - Lead as an alloying element in aluminum',
    },
    {
      value: '6(c)',
      label:
        '6(c) - Lead in copper alloy / brass (e.g., connectors, terminals)',
    },
    {
      value: '7(a)',
      label: '7(a) - Lead in high-melting-temperature solders',
    },
    {
      value: '7(c)-I',
      label:
        '7(c)-I - Lead in electronic ceramic components (e.g., capacitors)',
    },
    {
      value: '7(c)-II',
      label: '7(c)-II - Lead in dielectric ceramic in capacitors',
    },
    {
      value: 'OTHER',
      label: 'OTHER - Other / Custom Exemption',
    },
  ];

  const rohsExclusionReasons = [
    'Military / Aerospace Equipment',
    'Large-Scale Stationary Industrial Tool / Installation',
    'Active Implantable Medical Device',
    'Other Exempted Category (Article 2 Scope)',
  ];

  const criticalRawMaterialOptions = [
    'Cobalt',
    'Lithium',
    'Neodymium (Rare Earth)',
    'Tantalum',
    'Gallium',
    'Indium',
    'Magnesium',
    'Silicon Metal',
    'Other',
  ];
  const criticalRawMaterialDropdownOptions = criticalRawMaterialOptions.map(
    (option) => ({
      id: option,
      label: option,
    }),
  );

  const showExistingRohsSupportingDocument = Boolean(
    existingRohsSupportingDocumentName && !removeExistingRohsSupportingDocument,
  );
  const rohsSupportingDocumentDisplayName =
    existingRohsSupportingDocumentName ?? 'Uploaded file';
  const showSelectedRohsSupportingDocument = Boolean(
    form.rohsSupportingDocumentFile,
  );
  const showExistingPopsPfasDocument = Boolean(
    existingPopsPfasDocumentName && !removeExistingPopsPfasDocument,
  );
  const popsPfasDocumentDisplayName =
    existingPopsPfasDocumentName ?? 'Uploaded file';
  const showSelectedPopsPfasDocument = Boolean(form.popsPfasTestReportFile);
  const isIndustrial = form.categoryCode === 'INDUSTRIAL_MACHINERY';
  const isConstruction = form.categoryCode === 'CONSTRUCTION_PRODUCTS';
  const isFurniture = form.categoryCode === 'FURNITURE_MATTRESSES';
  const isChemicals = form.categoryCode === 'CHEMICALS';
  const showIndustrialRohsToggle = isIndustrial;
  const showRohsSection =
    isElectronics || (isIndustrial && form.containsElectricalComponents);
  const showCriticalRawMaterialsSection =
    isElectronics || isIndustrial || form.categoryCode === 'BATTERIES_STORAGE';
  const isBattery = form.categoryCode === 'BATTERIES_STORAGE';
  const showPopsPfasSection = isTextile || isBattery;
  const materialsContainWoodOrLeather = form.materials.some((material) =>
    /\b(wood|timber|oak|pine|beech|birch|plywood|veneer|leather|hide|suede)\b/i.test(
      material.material_name,
    ),
  );
  const processStagePlaceholder =
    form.categoryCode === 'TEXTILES_APPAREL'
      ? 'e.g., Fabric Dyeing & Finishing, Cut & Sew, Yarn Spinning'
      : form.categoryCode === 'ELECTRONICS_ICT'
        ? 'e.g., PCB Fabrication, SMT Assembly, Display Integration'
        : form.categoryCode === 'BATTERIES_STORAGE'
          ? 'e.g., Cell Stacking, Cathode Coating, Pack Assembly'
          : form.categoryCode === 'INDUSTRIAL_MACHINERY'
            ? 'Specify the proccess or part of the product: e.g. CNC Machining, Welding, Hydraulic unit, part number: xxxx-xxxx'
            : 'e.g., Sub-assembly, Surface Plating, Final Assembly';
  const processStageHelpText = processStagePlaceholder;
  const vocEmissionOptions = [
    { value: 'A_PLUS', label: 'A+' },
    { value: 'A', label: 'A' },
    { value: 'B', label: 'B' },
    { value: 'C', label: 'C' },
    { value: 'UNTESTED', label: 'Untested' },
  ];
  const flameRetardantOptions = [
    'DecaBDE',
    'TCEP',
    'TCPP',
    'TDCPP',
    'HBCD',
    'APP',
    'Melamine Polyphosphate',
    'Aluminum Hydroxide',
    'Magnesium Hydroxide',
    'Other',
  ].map((option) => ({ id: option, label: option }));
  const clpHazardPhraseOptions = [
    ['H200', 'Unstable explosive'],
    ['H220', 'Extremely flammable gas'],
    ['H226', 'Flammable liquid and vapour'],
    ['H272', 'May intensify fire; oxidiser'],
    ['H280', 'Contains gas under pressure'],
    ['H290', 'May be corrosive to metals'],
    ['H302', 'Harmful if swallowed'],
    ['H314', 'Causes severe skin burns and eye damage'],
    ['H315', 'Causes skin irritation'],
    ['H317', 'May cause an allergic skin reaction'],
    ['H318', 'Causes serious eye damage'],
    ['H319', 'Causes serious eye irritation'],
    ['H332', 'Harmful if inhaled'],
    ['H334', 'May cause allergy or asthma symptoms'],
    ['H335', 'May cause respiratory irritation'],
    ['H351', 'Suspected of causing cancer'],
    ['H360', 'May damage fertility or the unborn child'],
    ['H373', 'May cause damage to organs through prolonged exposure'],
    ['H400', 'Very toxic to aquatic life'],
    ['H410', 'Very toxic to aquatic life with long lasting effects'],
  ].map(([id, label]) => ({ id, label: `${id} - ${label}` }));
  const formatUfiCode = (value: string) => {
    const normalized = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 16);
    return normalized.replace(/(.{4})(?=.)/g, '$1-');
  };
  const renderHeaderWithTooltip = (
    label: string,
    tooltipId: string,
    tooltipText: string,
    ariaLabel: string,
  ) => (
    <div className="d-inline-flex align-items-center gap-1">
      <span>{label}</span>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
      >
        <button
          type="button"
          className="btn btn-link text-muted p-0 border-0 lh-1"
          aria-label={ariaLabel}
        >
          <i className="bi bi-info-circle-fill" />
        </button>
      </OverlayTrigger>
    </div>
  );

  return (
    <>
      {validationMessages.length > 0 && (
        <Alert variant="danger">
          <ul className="mb-0 ps-3">
            {validationMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 className="mb-0">
            {isTextile ? 'Fiber Composition' : 'Bill of Materials'} Breakdown
            {' *'}
          </h5>
          <div className="text-muted small">
            {isTextile
              ? 'Add fibers and their composition.'
              : 'Add the materials used in this product.'}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() =>
            updateForm('materials', [
              ...form.materials,
              {
                material_name: '',
                weight_percentage: '',
                recycled_content_percentage: '0',
                is_recyclable: false,
              },
            ])
          }
        >
          + Add Material
        </Button>
      </div>

      <Table responsive bordered size="sm" className="mb-2">
        <thead>
          <tr>
            <th>
              {renderHeaderWithTooltip(
                'Material Name',
                'material-name-tooltip',
                'Select or type the main material classification (e.g., Aluminum 6061, Polycarbonate, Recycled Cotton). Group minor sub-components under their primary material type.',
                'Show material name guidance',
              )}
            </th>
            <th>
              {renderHeaderWithTooltip(
                'Weight %',
                'weight-percentage-tooltip',
                'The mass fraction of this material relative to the total finished product weight. All row percentages combined must sum to exactly 100%.',
                'Show weight percentage guidance',
              )}
            </th>
            <th>
              {renderHeaderWithTooltip(
                'Recycled %',
                'recycled-percentage-tooltip',
                'The percentage of this specific material derived from recycled materials.',
                'Show recycled percentage guidance',
              )}
            </th>
            <th>Recyclable</th>
            <th aria-label="Remove material" />
          </tr>
        </thead>
        <tbody>
          {form.materials.map((material, index) => (
            <tr key={index}>
              <td>
                <Form.Control
                  size="sm"
                  value={material.material_name}
                  placeholder={isTextile ? 'Organic Cotton' : 'Aluminum 6061'}
                  onChange={(event) =>
                    updateMaterial(index, 'material_name', event.target.value)
                  }
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  min="0"
                  max="100"
                  value={material.weight_percentage}
                  onChange={(event) =>
                    updateMaterial(
                      index,
                      'weight_percentage',
                      event.target.value,
                    )
                  }
                />
              </td>
              <td>
                <Form.Control
                  size="sm"
                  type="number"
                  min="0"
                  max="100"
                  value={material.recycled_content_percentage}
                  onChange={(event) =>
                    updateMaterial(
                      index,
                      'recycled_content_percentage',
                      event.target.value,
                    )
                  }
                />
              </td>
              <td className="text-center">
                <Form.Check
                  aria-label="Material is recyclable"
                  checked={material.is_recyclable}
                  onChange={(event) =>
                    updateMaterial(index, 'is_recyclable', event.target.checked)
                  }
                />
              </td>
              <td className="text-center">
                <Button
                  variant="link"
                  className="p-0 text-danger"
                  disabled={form.materials.length === 1}
                  onClick={() =>
                    updateForm(
                      'materials',
                      form.materials.filter(
                        (_, materialIndex) => materialIndex !== index,
                      ),
                    )
                  }
                  aria-label="Remove material"
                >
                  <i className="bi bi-x-circle-fill" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div
        className={
          validationErrors.materials || bomTotal > 100
            ? 'text-danger small'
            : bomTotal < 100
              ? 'text-warning-emphasis small'
              : 'text-success small'
        }
      >
        {bomTotal > 100
          ? 'Total material breakdown cannot exceed 100%'
          : bomTotal < 100
            ? `BOM total: ${bomTotal}%. 100% is required for publication.`
            : 'BOM total: 100%.'}
      </div>
      {validationErrors.materials && (
        <div className="text-danger small mt-1">
          {validationErrors.materials}
        </div>
      )}

      <div className="border rounded bg-light p-3 mt-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <div className="fw-semibold">Packaging & Unit Container Data</div>
            <div className="text-muted small">
              Add packaging composition or declare that the item comes with no
              packaging.
            </div>
          </div>
          <Form.Check
            type="switch"
            label="Item includes packaging"
            checked={form.includeConsumerPackaging}
            onChange={(event) =>
              updateForm('includeConsumerPackaging', event.target.checked)
            }
          />
        </div>

        {!form.includeConsumerPackaging && (
          <div className="small fw-medium text-muted mb-2">
            Item comes with no packaging.
          </div>
        )}

        {form.includeConsumerPackaging && (
          <>
            <div className="d-flex justify-content-end mb-2">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={addPackagingItem}
              >
                + Add Packaging Item
              </Button>
            </div>

            {form.packagingItems.length === 0 ? (
              <div className="border rounded p-2 text-muted small">
                No packaging items added yet.
              </div>
            ) : (
              <div
                style={{
                  overflowX: 'auto',
                  overflowY: 'visible',
                  marginBottom: '1rem',
                }}
              >
                <Table bordered size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Packaging Category</th>
                      <th>Material Type</th>
                      <th>Weight (g)</th>
                      <th>
                        {renderHeaderWithTooltip(
                          'PCR %',
                          'packaging-pcr-tooltip',
                          'Post-Consumer Recycled content percentage (materials recycled from consumer waste, excluding factory scrap).',
                          'About PCR percentage',
                        )}
                      </th>
                      <th>Recyclability Status</th>
                      <th aria-label="Remove item" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.packagingItems.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.packaging_category}
                            onChange={(event) =>
                              updatePackagingItem(
                                index,
                                'packaging_category',
                                event.target
                                  .value as PackagingItem['packaging_category'],
                              )
                            }
                          >
                            <option value="">- Select -</option>
                            <option value="primary">
                              Primary / Sales Packaging (Immediate
                              container/wrapper)
                            </option>
                            <option value="secondary">
                              Secondary / Grouped Packaging (Bundles, outer
                              boxes)
                            </option>
                            <option value="tertiary">
                              Tertiary / Transport Packaging (Pallets, stretch
                              film, strapping)
                            </option>
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.material_type}
                            onChange={(event) =>
                              updatePackagingItem(
                                index,
                                'material_type',
                                event.target
                                  .value as PackagingItem['material_type'],
                              )
                            }
                          >
                            <option value="">- Select -</option>
                            <option value="pap20">
                              PAP 20 Corrugated Fiberboard
                            </option>
                            <option value="pap21">
                              PAP 21 Non-Corrugated Fiberboard
                            </option>
                            <option value="ldpe04">LDPE 04 Polyethylene</option>
                            <option value="pet01">PET 01</option>
                            <option value="wood">Wood / Pallet</option>
                            <option value="other">Other</option>
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Control
                            size="sm"
                            type="number"
                            min="0"
                            value={item.weight_grams}
                            onChange={(event) =>
                              updatePackagingItem(
                                index,
                                'weight_grams',
                                event.target.value,
                              )
                            }
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-1">
                            <Form.Control
                              size="sm"
                              type="number"
                              min="0"
                              max="100"
                              value={item.pcr_content_percent}
                              onChange={(event) =>
                                updatePackagingItem(
                                  index,
                                  'pcr_content_percent',
                                  event.target.value,
                                )
                              }
                            />
                            <span style={{ whiteSpace: 'nowrap' }}>%</span>
                          </div>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={item.recyclability_status}
                            onChange={(event) =>
                              updatePackagingItem(
                                index,
                                'recyclability_status',
                                event.target
                                  .value as PackagingItem['recyclability_status'],
                              )
                            }
                          >
                            <option value="">- Select -</option>
                            <option value="recyclable_100">
                              100% Recyclable
                            </option>
                            <option value="requires_separation">
                              Requires Separation
                            </option>
                            <option value="non_recyclable">
                              Non-recyclable
                            </option>
                          </Form.Select>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="link"
                            className="p-0 text-danger"
                            onClick={() =>
                              updateForm(
                                'packagingItems',
                                form.packagingItems.filter(
                                  (_, itemIndex) => itemIndex !== index,
                                ),
                              )
                            }
                            aria-label="Remove packaging item"
                          >
                            <i className="bi bi-x-circle-fill" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            <Form.Group className="mt-2">
              <Form.Label>
                Packaging EPR Producer Registration Number
              </Form.Label>
              <Form.Control
                size="sm"
                value={form.packagingEprRegistration}
                placeholder="e.g. LUCID ID, CITEO ID, or national registry number"
                onChange={(event) =>
                  updateForm('packagingEprRegistration', event.target.value)
                }
              />
              <Form.Text className="text-muted small">
                Optional. Extended Producer Responsibility registration
                identifier.
              </Form.Text>
            </Form.Group>
            {validationErrors.packagingItems && (
              <div className="text-danger small mt-2">
                {validationErrors.packagingItems}
              </div>
            )}
          </>
        )}
      </div>

      <div className="border rounded bg-light p-3 mt-4">
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <div className="fw-semibold">REACH SVHC Declaration</div>
            {/* <div className="text-muted small">
              Declare substances of very high concern present above 0.1% w/w.
            </div> */}
            <div className="text-muted small">
              EU REACH Article 33 requires manufacturers to declare if any
              Substance of Very High Concern (SVHC) from the{' '}
              <a
                href="https://chem.echa.europa.eu/obligation-lists/candidateList"
                target="_blank"
                rel="noopener noreferrer"
              >
                ECHA Candidate List
              </a>{' '}
              is present above 0.1% weight-by-weight (w/w) in any individual
              article/component.
            </div>
          </div>
        </div>
        <Form.Group>
          <Form.Label className="visually-hidden">REACH SVHC Status</Form.Label>
          <div className="d-flex flex-wrap gap-3">
            <Form.Check
              inline
              type="radio"
              name="svhcStatus"
              id="svhcNone"
              label="No SVHCs present"
              checked={form.svhcStatus === 'none'}
              onChange={() => {
                updateForm('svhcStatus', 'none');
                updateForm('svhcDeclarations', []);
              }}
            />
            <Form.Check
              inline
              type="radio"
              name="svhcStatus"
              id="svhcContains"
              label="Contains SVHC > 0.1% w/w"
              checked={form.svhcStatus === 'contains'}
              onChange={() => {
                updateForm('svhcStatus', 'contains');
                if (form.svhcDeclarations.length === 0) {
                  addSvhcDeclaration();
                }
              }}
            />
          </div>
          {validationErrors.svhcStatus && (
            <div className="text-danger small mt-2">
              {validationErrors.svhcStatus}
            </div>
          )}
        </Form.Group>

        {form.svhcStatus === 'contains' && (
          <>
            <datalist id="echaCandidateSubstances">
              {echaCandidateSubstances.map((substance) => (
                <option
                  key={substance.substance_name}
                  value={substance.substance_name}
                >
                  {[substance.ec_number, substance.cas_number]
                    .filter(Boolean)
                    .join(' / ')}
                </option>
              ))}
            </datalist>
            <div className="d-flex justify-content-end mb-2 mt-3">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={addSvhcDeclaration}
              >
                + Add Substance
              </Button>
            </div>
            <Table responsive bordered size="sm" className="mb-0 bg-white">
              <thead>
                <tr>
                  <th>Substance Name</th>
                  <th>
                    {renderHeaderWithTooltip(
                      'CAS / EC Number',
                      'cas-ec-number-tooltip',
                      'The numerical identifier assigned to the chemical substance (e.g., EC 284-366-9 / CAS 84852-53-9).',
                      'Show CAS or EC number guidance',
                    )}
                  </th>
                  <th>
                    {renderHeaderWithTooltip(
                      'Present In Component',
                      'present-in-component-tooltip',
                      'Identify the specific sub-part or homogeneous material containing the substance (e.g., Power cable casing, Internal circuit solder, Rubber gasket).',
                      'Show component location guidance',
                    )}
                  </th>
                  <th>
                    {renderHeaderWithTooltip(
                      'SCIP Dossier Number',
                      'scip-dossier-number-tooltip',
                      'The unique submission UUID generated when submitting your article declaration to ECHA’s SCIP database.',
                      'Show SCIP dossier number guidance',
                    )}
                  </th>
                  <th aria-label="Remove substance" />
                </tr>
              </thead>
              <tbody>
                {form.svhcDeclarations.map((declaration, index) => (
                  <tr key={index}>
                    <td>
                      <Form.Control
                        size="sm"
                        list="echaCandidateSubstances"
                        value={declaration.substance_name}
                        placeholder="Search or enter substance"
                        onChange={(event) =>
                          handleSvhcSubstanceChange(index, event.target.value)
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={declaration.cas_ec_number}
                        placeholder="CAS or EC number"
                        onChange={(event) =>
                          updateSvhcDeclaration(
                            index,
                            'cas_ec_number',
                            event.target.value,
                          )
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={declaration.present_in_component}
                        placeholder="e.g. Cable insulation"
                        onChange={(event) =>
                          updateSvhcDeclaration(
                            index,
                            'present_in_component',
                            event.target.value,
                          )
                        }
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={declaration.scip_dossier_number}
                        onChange={(event) =>
                          updateSvhcDeclaration(
                            index,
                            'scip_dossier_number',
                            event.target.value,
                          )
                        }
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="link"
                        className="p-0 text-danger"
                        aria-label="Remove substance"
                        onClick={() =>
                          updateForm(
                            'svhcDeclarations',
                            form.svhcDeclarations.filter(
                              (_, declarationIndex) =>
                                declarationIndex !== index,
                            ),
                          )
                        }
                      >
                        <i className="bi bi-x-circle-fill" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {validationErrors.svhcDeclarations && (
              <div className="text-danger small mt-2">
                {validationErrors.svhcDeclarations}
              </div>
            )}
          </>
        )}
      </div>

      <Row className="g-3 mt-1">
        {(showIndustrialRohsToggle || showRohsSection) && (
          <Col xs={12}>
            <div className="border rounded bg-light p-3">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <div className="fw-semibold">RoHS Compliance</div>
                  <div className="text-muted small">
                    {isIndustrial
                      ? 'Enable this section only when the machinery contains electrical or electronic components in scope of RoHS Directive 2011/65/EU.'
                      : 'Capture RoHS status, exemptions, exclusions, and supporting evidence for electronic equipment.'}
                  </div>
                </div>
              </div>
              <Row className="g-3">
                {showIndustrialRohsToggle && (
                  <Col xs={12}>
                    <Form.Check
                      type="switch"
                      id="containsElectricalComponents"
                      label="Contains electrical or electronic components (RoHS Directive 2011/65/EU Applies)"
                      checked={form.containsElectricalComponents}
                      onChange={(event) => {
                        const nextValue = event.target.checked;
                        updateForm('containsElectricalComponents', nextValue);

                        if (nextValue) {
                          return;
                        }

                        updateForm('rohsStatus', '');
                        updateForm('rohsExemptionCode', '');
                        updateForm('customExemptionDetails', '');
                        updateForm('rohsExclusionReason', '');
                        updateForm('rohsSupportingDocumentFile', null);
                        void onRemoveExistingRohsSupportingDocument();
                      }}
                    />
                  </Col>
                )}
                {showRohsSection && (
                  <>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>RoHS Compliance Status *</Form.Label>
                        <Form.Select
                          value={form.rohsStatus}
                          isInvalid={Boolean(validationErrors.rohsStatus)}
                          onChange={(event) => {
                            const nextStatus = event.target
                              .value as ProductForm['rohsStatus'];

                            updateForm('rohsStatus', nextStatus);

                            if (nextStatus !== 'with_exemption') {
                              updateForm('rohsExemptionCode', '');
                              updateForm('customExemptionDetails', '');
                            }

                            if (nextStatus !== 'not_applicable') {
                              updateForm('rohsExclusionReason', '');
                            }

                            if (
                              nextStatus !== 'without_exemption' &&
                              nextStatus !== 'with_exemption'
                            ) {
                              updateForm('rohsSupportingDocumentFile', null);
                            }
                          }}
                        >
                          <option value="">Select status</option>
                          <option value="without_exemption">
                            Compliant without Exemption
                          </option>
                          <option value="with_exemption">
                            Compliant with Exemption
                          </option>
                          <option value="not_applicable">Not Applicable</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.rohsStatus}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    {form.rohsStatus === 'not_applicable' && (
                      <Col xs={12}>
                        <Alert variant="warning" className="mb-0">
                          RoHS applies to all EEE products. Select a valid RoHS
                          exclusion reason if out of scope.
                        </Alert>
                      </Col>
                    )}
                    {form.rohsStatus === 'with_exemption' && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>RoHS Exemption Code *</Form.Label>
                          <Form.Select
                            value={form.rohsExemptionCode}
                            isInvalid={Boolean(
                              validationErrors.rohsExemptionCode,
                            )}
                            onChange={(event) => {
                              const nextCode = event.target.value;
                              updateForm('rohsExemptionCode', nextCode);

                              if (nextCode !== 'OTHER') {
                                updateForm('customExemptionDetails', '');
                              }
                            }}
                            required
                          >
                            <option value="">Select exemption</option>
                            {rohsExemptionOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.rohsExemptionCode}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}
                    {form.rohsStatus === 'with_exemption' &&
                      form.rohsExemptionCode === 'OTHER' && (
                        <Col xs={12}>
                          <Form.Group>
                            <Form.Label>Custom Exemption Details *</Form.Label>
                            <Form.Control
                              value={form.customExemptionDetails}
                              isInvalid={Boolean(
                                validationErrors.customExemptionDetails,
                              )}
                              placeholder="Enter Annex III or IV clause number, e.g., Exemption 15"
                              onChange={(event) =>
                                updateForm(
                                  'customExemptionDetails',
                                  event.target.value,
                                )
                              }
                              required
                            />
                            <Form.Control.Feedback type="invalid">
                              {validationErrors.customExemptionDetails}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                    {form.rohsStatus === 'not_applicable' && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>RoHS Exclusion Reason *</Form.Label>
                          <Form.Select
                            value={form.rohsExclusionReason}
                            isInvalid={Boolean(
                              validationErrors.rohsExclusionReason,
                            )}
                            onChange={(event) =>
                              updateForm(
                                'rohsExclusionReason',
                                event.target.value,
                              )
                            }
                            required
                          >
                            <option value="">Select exclusion reason</option>
                            {rohsExclusionReasons.map((reason) => (
                              <option key={reason} value={reason}>
                                {reason}
                              </option>
                            ))}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {validationErrors.rohsExclusionReason}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    )}
                    {(form.rohsStatus === 'without_exemption' ||
                      form.rohsStatus === 'with_exemption') && (
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>
                            RoHS Supporting Document (
                            <a
                              href="https://supplypassport.co/compliance-declarations/?type=rohs"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              RoHS Declaration Tool
                            </a>
                            ) *
                          </Form.Label>
                          {showExistingRohsSupportingDocument ? (
                            <div className="small">
                              <div className="text-muted mb-1">
                                Current uploaded file
                              </div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                {existingRohsSupportingDocumentSignedUrl ? (
                                  <a
                                    href={
                                      existingRohsSupportingDocumentSignedUrl
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {shortenFileName(
                                      rohsSupportingDocumentDisplayName,
                                    )}
                                  </a>
                                ) : (
                                  <span>
                                    {shortenFileName(
                                      rohsSupportingDocumentDisplayName,
                                    )}
                                  </span>
                                )}
                                <Button
                                  size="sm"
                                  variant="link text-danger p-0"
                                  onClick={() =>
                                    void onRemoveExistingRohsSupportingDocument()
                                  }
                                >
                                  <i className="bi bi-x-circle-fill"></i>
                                </Button>
                              </div>
                            </div>
                          ) : showSelectedRohsSupportingDocument ? (
                            <div className="small">
                              <div className="text-muted mb-1">
                                Current uploaded file
                              </div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="text-muted">
                                  {shortenFileName(
                                    form.rohsSupportingDocumentFile?.name ??
                                      'Uploaded file',
                                  )}
                                </span>
                                <Button
                                  size="sm"
                                  variant="link text-danger p-0"
                                  onClick={() =>
                                    void onRohsSupportingDocumentFileChange(
                                      null,
                                    )
                                  }
                                >
                                  <i className="bi bi-x-circle-fill"></i>
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <Form.Control
                                type="file"
                                accept=".pdf,.doc,.docx"
                                isInvalid={Boolean(
                                  validationErrors.rohsSupportingDocumentFile,
                                )}
                                onChange={(event) =>
                                  void onRohsSupportingDocumentFileChange(
                                    (event.target as HTMLInputElement)
                                      .files?.[0] ?? null,
                                  )
                                }
                              />
                              <Form.Control.Feedback type="invalid">
                                {validationErrors.rohsSupportingDocumentFile}
                              </Form.Control.Feedback>
                              <div className="text-muted small mt-2">
                                Upload a file to attach it to this product.
                              </div>
                            </>
                          )}
                        </Form.Group>
                      </Col>
                    )}
                  </>
                )}
              </Row>
            </div>
          </Col>
        )}
        {isConstruction && (
          <Col xs={12}>
            <div className="border rounded bg-light p-3">
              <div className="fw-semibold mb-3">
                Construction Product Compliance
              </div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>VOC Emission Class</Form.Label>
                    <Form.Select
                      value={form.vocEmissionClass}
                      onChange={(event) =>
                        updateForm(
                          'vocEmissionClass',
                          event.target.value as ProductForm['vocEmissionClass'],
                        )
                      }
                    >
                      <option value="">Select VOC class</option>
                      {vocEmissionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Col>
        )}
        {showCriticalRawMaterialsSection && (
          <Col xs={12}>
            <div className="border rounded bg-light p-3">
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                <div>
                  <div className="fw-semibold">Critical Raw Materials</div>
                  <div className="text-muted small">
                    Record critical raw material presence for products that
                    require CRM disclosure.
                  </div>
                </div>
              </div>
              <Row className="g-3">
                <Col xs={12} className="d-flex align-items-end">
                  <Form.Check
                    type="switch"
                    id="criticalRawMaterials"
                    label="Critical Raw Materials (CRM) present"
                    checked={form.criticalRawMaterials}
                    onChange={(event) => {
                      updateForm('criticalRawMaterials', event.target.checked);
                      if (!event.target.checked) {
                        updateForm('criticalRawMaterialsList', []);
                      }
                    }}
                  />
                </Col>
                {form.criticalRawMaterials && (
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Critical Raw Materials Present *</Form.Label>
                      <DropdownSelect
                        options={criticalRawMaterialDropdownOptions}
                        selectedIds={form.criticalRawMaterialsList}
                        onValueChanged={(selected) => {
                          const selectedOptions = Array.isArray(selected)
                            ? selected
                            : [selected];

                          updateForm(
                            'criticalRawMaterialsList',
                            selectedOptions.map((option) => option.id),
                          );
                        }}
                        placeholder="Select critical raw materials"
                        multiselect
                        full
                        hideSearch
                      />
                      {validationErrors.criticalRawMaterialsList && (
                        <div className="text-danger small mt-2">
                          {validationErrors.criticalRawMaterialsList}
                        </div>
                      )}
                      <Form.Text className="text-muted small">
                        Select one or more materials.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
                {(isElectronics || isIndustrial) && (
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>
                        {renderHeaderWithTooltip(
                          'WEEE Registration Number',
                          'weee-registration-number-tooltip',
                          'Your official Extended Producer Responsibility (EPR) identifier issued by a national registry (e.g., Stiftung EAR in Germany, SYDEREP in France) under EU Directive 2012/19/EU. Proves legal compliance for placing electronic equipment on the EU market.',
                          'Show WEEE registration number guidance',
                        )}
                      </Form.Label>
                      <Form.Control
                        isInvalid={Boolean(
                          validationErrors.weeeRegistrationNumber,
                        )}
                        value={form.weeeRegistrationNumber}
                        onChange={(event) =>
                          updateForm(
                            'weeeRegistrationNumber',
                            event.target.value,
                          )
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.weeeRegistrationNumber}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted small">
                        Format: National Producer Registration Number (e.g., DE
                        12345678 for Germany EAR, or UK WEEE Producer ID).
                      </Form.Text>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        )}
        {isFurniture && (
          <Col xs={12}>
            <div className="border rounded bg-light p-3">
              <div className="fw-semibold mb-3">Furniture Compliance</div>
              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>
                      EUDR DDS Reference
                      {materialsContainWoodOrLeather ? ' *' : ''}
                    </Form.Label>
                    <Form.Control
                      value={form.eudrDdsReference}
                      isInvalid={Boolean(validationErrors.eudrDdsReference)}
                      placeholder="Enter Due Diligence Statement reference"
                      onChange={(event) =>
                        updateForm('eudrDdsReference', event.target.value)
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.eudrDdsReference}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted small">
                      {materialsContainWoodOrLeather
                        ? 'Required because the material list currently includes wood or leather.'
                        : 'Required when the product contains wood or leather materials.'}
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Check
                    type="switch"
                    id="flameRetardantsPresent"
                    label="Flame retardants present"
                    checked={form.flameRetardantsPresent}
                    onChange={(event) => {
                      updateForm(
                        'flameRetardantsPresent',
                        event.target.checked,
                      );
                      if (!event.target.checked) {
                        updateForm('flameRetardantChemicalNames', []);
                      }
                    }}
                  />
                </Col>
                {form.flameRetardantsPresent && (
                  <Col xs={12}>
                    <Form.Group>
                      <Form.Label>Flame Retardant Chemicals *</Form.Label>
                      <DropdownSelect
                        options={flameRetardantOptions}
                        selectedIds={form.flameRetardantChemicalNames}
                        onValueChanged={(selected) => {
                          const selectedOptions = Array.isArray(selected)
                            ? selected
                            : [selected];

                          updateForm(
                            'flameRetardantChemicalNames',
                            selectedOptions.map((option) => option.id),
                          );
                        }}
                        placeholder="Select flame retardant chemicals"
                        multiselect
                        full
                      />
                      {validationErrors.flameRetardantChemicalNames && (
                        <div className="text-danger small mt-2">
                          {validationErrors.flameRetardantChemicalNames}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        )}
        {showPopsPfasSection && (
          <Col xs={12}>
            <div className="border rounded bg-light p-3 mt-3">
              <h6 className="mb-3">POPs & PFAS Declaration</h6>
              <div className="text-muted small mb-3">
                {isBattery
                  ? 'Assess fluorinated battery chemistry inputs such as fluoropolymer binders, separator coatings, electrolyte salts, or additives, and document any POPs / PFAS concerns.'
                  : 'Document textile POPs / PFAS compliance status and supporting evidence.'}
              </div>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Compliance Status</Form.Label>
                  <Form.Select
                    value={form.popsPfasStatus}
                    onChange={(event) => {
                      const value = event.target
                        .value as ProductForm['popsPfasStatus'];
                      updateForm('popsPfasStatus', value);
                    }}
                  >
                    <option value="">Not Verified Yet</option>
                    <option value="verified_compliant">
                      Verified Compliant (No prohibited POPs/PFAS)
                    </option>
                    <option value="contains_restricted">
                      Contains Restricted PFAS / Exemption Applies
                    </option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {form.popsPfasStatus === 'verified_compliant' && (
                <Col xs={12} className="mt-3">
                  <Form.Group>
                    <Form.Label>
                      Attach PFAS/POPs Test Report or Supplier Self-Declaration
                      (Required for DPP) *
                    </Form.Label>
                    {showExistingPopsPfasDocument ? (
                      <div className="small">
                        <div className="text-muted mb-1">
                          Current uploaded file
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          {existingPopsPfasDocumentSignedUrl ? (
                            <a
                              href={existingPopsPfasDocumentSignedUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {shortenFileName(popsPfasDocumentDisplayName)}
                            </a>
                          ) : (
                            <span>
                              {shortenFileName(popsPfasDocumentDisplayName)}
                            </span>
                          )}
                          <Button
                            size="sm"
                            variant="link text-danger p-0"
                            onClick={() =>
                              void onRemoveExistingPopsPfasDocument()
                            }
                          >
                            <i className="bi bi-x-circle-fill"></i>
                          </Button>
                        </div>
                      </div>
                    ) : showSelectedPopsPfasDocument ? (
                      <div className="small">
                        <div className="text-muted mb-1">
                          Current uploaded file
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <span className="text-muted">
                            {shortenFileName(
                              form.popsPfasTestReportFile?.name ??
                                'Uploaded file',
                            )}
                          </span>
                          <Button
                            size="sm"
                            variant="link text-danger p-0"
                            onClick={() =>
                              void onPopsPfasTestReportFileChange(null)
                            }
                          >
                            <i className="bi bi-x-circle-fill"></i>
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          isInvalid={Boolean(
                            validationErrors.popsPfasTestReportFile,
                          )}
                          onChange={(event) =>
                            void onPopsPfasTestReportFileChange(
                              (event.target as HTMLInputElement).files?.[0] ??
                                null,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.popsPfasTestReportFile}
                        </Form.Control.Feedback>
                        <div className="text-muted small mt-2">
                          Upload a file to attach it to this product.
                        </div>
                      </>
                    )}
                  </Form.Group>
                </Col>
              )}

              {form.popsPfasStatus === 'contains_restricted' && (
                <Col xs={12} className="mt-3">
                  <Form.Group>
                    <Form.Label>
                      {isBattery
                        ? 'Specify the fluorinated substance, chemistry component, and applicable exemption clause *'
                        : 'Specify substance and applicable exemption clause *'}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      isInvalid={Boolean(
                        validationErrors.popsPfasExemptionDetails,
                      )}
                      placeholder={
                        isBattery
                          ? 'Enter substance name(s), battery component location (e.g. PVDF cathode binder, electrolyte additive), and the relevant exemption clause or regulation reference...'
                          : 'Enter substance name(s) and the relevant exemption clause or regulation reference...'
                      }
                      value={form.popsPfasExemptionDetails}
                      onChange={(event) =>
                        updateForm(
                          'popsPfasExemptionDetails',
                          event.target.value,
                        )
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.popsPfasExemptionDetails}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
            </div>
          </Col>
        )}
        {isChemicals && (
          <Col xs={12}>
            <div className="border rounded bg-light p-3 mt-3">
              <div className="fw-semibold mb-3">Chemical Compliance</div>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>UFI Code</Form.Label>
                    <Form.Control
                      value={form.ufiCode}
                      isInvalid={Boolean(validationErrors.ufiCode)}
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      onChange={(event) =>
                        updateForm('ufiCode', formatUfiCode(event.target.value))
                      }
                    />
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.ufiCode}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>CLP Hazard Phrases</Form.Label>
                    <DropdownSelect
                      options={clpHazardPhraseOptions}
                      selectedIds={form.clpHazardPhrases}
                      onValueChanged={(selected) => {
                        const selectedOptions = Array.isArray(selected)
                          ? selected
                          : [selected];

                        updateForm(
                          'clpHazardPhrases',
                          selectedOptions.map((option) => option.id),
                        );
                      }}
                      placeholder="Select hazard phrases"
                      multiselect
                      full
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Col>
        )}

        <Col xs={12}>
          {!isTextile && (
            <Form.Check
              type="switch"
              id="includeSupplyChainTraceability"
              label="Add supply chain traceability data"
              checked={form.includeSupplyChainTraceability}
              onChange={(event) =>
                updateForm(
                  'includeSupplyChainTraceability',
                  event.target.checked,
                )
              }
            />
          )}

          {(isTextile || form.includeSupplyChainTraceability) && (
            <div className={isTextile ? undefined : 'mt-3'}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <Form.Label className="mb-0">
                    Supply Chain Traceability{' '}
                    <span className="text-muted small">
                      [Restricted access to authorized users only]
                    </span>
                  </Form.Label>
                  <div className="text-muted small">
                    Record direct manufacturers, component suppliers, and raw
                    material processors.
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={addSupplyChainSupplier}
                >
                  + Add Supplier
                </Button>
              </div>

              {form.supplyChainTraceability.length === 0 ? (
                <div className="border rounded p-3 text-muted small">
                  No supply chain suppliers added yet.
                </div>
              ) : (
                <div style={{ overflow: 'visible' }}>
                  <Table
                    bordered
                    size="sm"
                    className="mb-0"
                    style={{ minWidth: '100%' }}
                  >
                    <thead>
                      <tr>
                        <th>Tier</th>
                        <th>
                          <div className="d-inline-flex align-items-center gap-1">
                            <span>Company Name</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="company-name-tooltip">
                                  The company name will only be visible to users
                                  with RESTRICTED access - you will approve the
                                  access level upon request.
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="btn btn-link text-muted p-0 border-0 lh-1"
                                aria-label="Show company name visibility guidance"
                              >
                                <i className="bi bi-info-circle-fill" />
                              </button>
                            </OverlayTrigger>
                          </div>
                        </th>
                        <th>Country</th>
                        <th>
                          <div className="d-inline-flex align-items-center gap-1">
                            <span>Facility ID / Address</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="facility-id-tooltip">
                                  Enter a recognized facility identifier (e.g.,
                                  GS1 Global Location Number (GLN), Open Supply
                                  Hub OS ID) or the physical city and street
                                  address.
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="btn btn-link text-muted p-0 border-0 lh-1"
                                aria-label="Show facility identifier examples"
                              >
                                <i className="bi bi-info-circle-fill" />
                              </button>
                            </OverlayTrigger>
                          </div>
                        </th>
                        <th>
                          <div className="d-inline-flex align-items-center gap-1">
                            <span>Process / Stage</span>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id="process-stage-tooltip">
                                  {processStageHelpText}
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="btn btn-link text-muted p-0 border-0 lh-1"
                                aria-label="Show process or stage examples"
                              >
                                <i className="bi bi-info-circle-fill" />
                              </button>
                            </OverlayTrigger>
                          </div>
                        </th>
                        <th aria-label="Remove supplier" />
                      </tr>
                    </thead>
                    <tbody>
                      {form.supplyChainTraceability.map((supplier, index) => (
                        <tr key={index}>
                          <td>
                            <Form.Select
                              size="sm"
                              value={supplier.tier}
                              onChange={(event) =>
                                updateSupplyChainSupplier(
                                  index,
                                  'tier',
                                  event.target
                                    .value as SupplyChainTraceabilityRow['tier'],
                                )
                              }
                            >
                              <option value="tier_1">
                                Tier 1 (Direct Manufacturer / Final Assembly)
                              </option>
                              <option value="tier_2">
                                Tier 2 (Component / Fabric / Sub-assembly)
                              </option>
                              <option value="tier_3">
                                Tier 3 (Raw Material / Processing)
                              </option>
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={supplier.company_name}
                              onChange={(event) =>
                                updateSupplyChainSupplier(
                                  index,
                                  'company_name',
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <CountrySelect
                              full
                              value={supplier.country_code}
                              onChange={(value) =>
                                updateSupplyChainSupplier(
                                  index,
                                  'country_code',
                                  value as string,
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={supplier.facility_id_or_address}
                              placeholder="GLN, OSH ID, or City/Address"
                              onChange={(event) =>
                                updateSupplyChainSupplier(
                                  index,
                                  'facility_id_or_address',
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <Form.Control
                              size="sm"
                              value={supplier.process_component}
                              placeholder={processStagePlaceholder}
                              onChange={(event) =>
                                updateSupplyChainSupplier(
                                  index,
                                  'process_component',
                                  event.target.value,
                                )
                              }
                            />
                          </td>
                          <td>
                            <Button
                              variant="link"
                              className="p-0 text-danger"
                              aria-label="Remove supplier"
                              onClick={() =>
                                updateForm(
                                  'supplyChainTraceability',
                                  form.supplyChainTraceability.filter(
                                    (_, supplierIndex) =>
                                      supplierIndex !== index,
                                  ),
                                )
                              }
                            >
                              <i className="bi bi-x-circle-fill" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
}
